import React, { useCallback, useEffect, useState } from "react";
import {
  formState,
  FormStore,
  formUniversalTaggedState,
} from "../../services/form.store";
import {
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  makeStyles,
  Menu,
  Typography,
} from "@material-ui/core";
import { TreeItem, TreeView } from "@material-ui/lab";
import ChevronRightIcon from "@material-ui/icons/ChevronRight";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import {
  CONDITIONAL_FORM,
  IS_CONDITIONAL_FORM_ENABLED,
} from "../../utility/Constant";

const useStyles = makeStyles({
  treeItem: {
    "& .MuiTreeItem-label": {
      transition: "background 0.2s ease",
      borderBottom: "1px solid #e0e0e0",
    },
    "& .MuiTreeItem-label:hover": { backgroundColor: "#f5f5f5" },
  },
});

const ConditionalFormList = ({ element }) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectAllChecked, setSelectAllChecked] = useState(true);
  const [conditionalFormQuestions, setConditionalFormQuestions] = useState([]);
  const [universalTagging, setUniversalTagging] = useState([]);
  const [formUniversalTagged, setFormUniversalTagged] = useState();
  const classes = useStyles();

  const getElementNode = (elementId, children) => {
    if (!children) return undefined;
    for (const child of children) {
      if (child?.elementId === elementId) {
        return child;
      }
      const found = getElementNode(elementId, child?.children);
      if (found) return found;
    }
  };

  const updateTreeSelections = (options, taggingName, level, previousLevel) => {
    options?.forEach((option) => {
      if (option?.name === taggingName && option?.level === level) {
        if (level === CONDITIONAL_FORM.FIRST_LEVEL) {
          option.selected = true;
        } else if (previousLevel.selected) {
          option.selected = true;
        }
        return;
      } else {
        updateTreeSelections(option?.options, taggingName, level, option);
      }
    });
  };

  const transformHierarchy = (options) => {
    options?.forEach((option) => {
      option.selected = true;
      if (option?.options?.length) transformHierarchy(option?.options);
    });
  };

  useEffect(() => {
    const sub1 = formUniversalTaggedState.subscribe((selected) => {
      setFormUniversalTagged(selected);
    });
    const sub2 = formState.subscribe((response) => {
      const universalHierachy = JSON.parse(
        JSON.stringify(response?.universalHierarchy),
      );
      const elementNode = getElementNode(
        element?.elementId,
        response?.children,
      );

      if (elementNode?.conditionalSelected) {
        transformHierarchy(universalHierachy?.options);
        setSelectAllChecked(true);
        setConditionalFormQuestions(universalHierachy);
      } else if (formUniversalTagged) {
        if (elementNode?.universalTagging?.length) {
          elementNode?.universalTagging?.forEach((universalTag) => {
            universalTag
              ?.split(CONDITIONAL_FORM.UNIVERSAL_TAGGING_SPLIT)
              ?.forEach((taggingName, index) => {
                updateTreeSelections(
                  universalHierachy?.options,
                  taggingName,
                  index + 1,
                );
              });
          });
          setSelectAllChecked(elementNode?.allHierarchySelected);
          setConditionalFormQuestions(universalHierachy);
        } else {
          setConditionalFormQuestions(universalHierachy);
          setSelectAllChecked(elementNode?.allHierarchySelected ?? false);
        }
      } else {
        setSelectAllChecked(false);
        setConditionalFormQuestions(universalHierachy);
      }
    });

    return () => {
      sub1.unsubscribe();
      sub2.unsubscribe();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formUniversalTagged]);

  useEffect(() => {
    if (
      (!formUniversalTagged && universalTagging?.length) ||
      element?.conditionalSelected
    ) {
      FormStore.sharedInstance().addUniversalTagging(
        element?.elementId,
        universalTagging,
        selectAllChecked,
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [universalTagging]);

  const frameUniversalTagging = useCallback(
    (options, levelTaggings, parentId) => {
      options?.forEach((option) => {
        if (option?.id === parentId) {
          levelTaggings?.push(option?.name);
          if (option?.level !== CONDITIONAL_FORM.FIRST_LEVEL) {
            frameUniversalTagging(
              conditionalFormQuestions?.options,
              levelTaggings,
              option?.parentId,
            );
          }
        } else {
          frameUniversalTagging(option?.options, levelTaggings, parentId);
        }
      });
    },
    [conditionalFormQuestions?.options],
  );

  const computeUniversalTagging = useCallback(
    (options, totalLevels, universalTaggingData) => {
      options?.forEach((option) => {
        if (option?.level === totalLevels && option?.selected) {
          const levelTaggings = [];
          levelTaggings?.push(option?.name);
          frameUniversalTagging(
            conditionalFormQuestions?.options,
            levelTaggings,
            option?.parentId,
          );
          if (levelTaggings?.length) {
            universalTaggingData?.push(
              levelTaggings
                ?.reverse()
                ?.join(CONDITIONAL_FORM.UNIVERSAL_TAGGING_SPLIT),
            );
          }
        } else if (option?.options) {
          computeUniversalTagging(
            option?.options,
            totalLevels,
            universalTaggingData,
          );
        }
      });
      return universalTaggingData;
    },
    [conditionalFormQuestions?.options, frameUniversalTagging],
  );

  useEffect(() => {
    setUniversalTagging(() => {
      const universalTaggingData = [];
      return computeUniversalTagging(
        conditionalFormQuestions?.options,
        conditionalFormQuestions?.totalLevels,
        universalTaggingData,
      );
    });
  }, [computeUniversalTagging, conditionalFormQuestions]);

  const attachUniversalTagging = () => {
    FormStore.sharedInstance().updateUniversalTagging(
      element?.elementId,
      universalTagging,
      selectAllChecked,
    );
    setAnchorEl(null);
  };

  const toggleSelectedAllField = (options, checked) =>
    options?.map((option) => ({
      ...option,
      selected: checked,
      options: toggleSelectedAllField(option?.options, checked),
    }));

  const handleSelectAll = (event, checked) => {
    event.stopPropagation();
    setSelectAllChecked(checked);
    setConditionalFormQuestions((prevState) => ({
      ...prevState,
      options: toggleSelectedAllField(prevState?.options, checked),
    }));
  };

  const allHierarchySelected = (data) => {
    const getLeafNodes = (node) => {
      if (!node.options || node.options.length === 0) {
        return [node];
      }
      return node.options.flatMap(getLeafNodes);
    };
    const lastNodes = data.options.flatMap(getLeafNodes);
    return lastNodes.every((leaf) => leaf.selected === true);
  };

  const buildNodeMap = (options, map = {}) => {
    options.forEach((opt) => {
      map[opt.id] = opt;
      if (opt.options && opt.options.length > 0) {
        buildNodeMap(opt.options, map);
      }
    });
    return map;
  };

  const updateNodeById = (node, id, checked) => {
    if (node.id === id) {
      return { ...node, selected: checked };
    }
    return {
      ...node,
      options: (node.options || []).map((child) =>
        updateNodeById(child, id, checked),
      ),
    };
  };

  const updateConditionalFormQuestions = (
    data,
    nodeId,
    checked,
    shouldUpdateParent,
  ) => {
    const nodeMap = buildNodeMap(data.options);
    let newData = JSON.parse(JSON.stringify(data));
    const updateChain = (targetId) => {
      newData = updateNodeById(newData, targetId, checked);
      if (shouldUpdateParent) {
        let current = nodeMap[targetId];
        while (current && current.parentId) {
          newData = updateNodeById(newData, current.parentId, checked);
          current = nodeMap[current.parentId];
        }
      }
    };
    updateChain(nodeId);
    return newData;
  };

  const handleSelect = (checked, options, option) => {
    const clonedOptions = structuredClone(options);
    clonedOptions?.forEach((opt) => {
      if (opt.id === option.id) {
        opt.selected = checked;
      }
    });
    const shouldUpdateParent = clonedOptions?.every(
      (option) => option.selected === checked,
    );
    const updatedData = updateConditionalFormQuestions(
      conditionalFormQuestions,
      option.id,
      checked,
      shouldUpdateParent,
    );
    const allSelected = allHierarchySelected(updatedData);
    setConditionalFormQuestions(updatedData);
    setSelectAllChecked(allSelected);
  };

  const renderLevels = (options) =>
    options?.map((option) => (
      <TreeItem
        key={option?.id}
        nodeId={option?.id}
        classes={{ root: classes.treeItem }}
        label={
          <FormControlLabel
            key={`${option.id}-${option.selected}`}
            style={{ marginRight: 0 }}
            control={
              !option?.options?.length ? (
                <Checkbox
                  checked={option?.selected}
                  onClick={(event) => event.stopPropagation()}
                  onChange={(event) => {
                    event.stopPropagation();
                    handleSelect(event.target.checked, options, option);
                  }}
                  style={{ padding: "0" }}
                  size="small"
                />
              ) : (
                <Checkbox
                  size="small"
                  style={{ display: "none" }}
                  inputProps={{ readOnly: true }}
                />
              )
            }
            label={
              <Typography style={{ margin: "8px 16px" }}>
                {option?.name}
              </Typography>
            }
          />
        }
      >
        {option?.options && renderLevels(option?.options)}
      </TreeItem>
    ));

  const collectIds = (options) => {
    let ids = [];
    options?.forEach((option) => {
      ids.push(option.id);
      if (option.options && option.options.length > 0) {
        ids = ids.concat(collectIds(option.options));
      }
    });
    return ids;
  };

  return (
    <>
      {IS_CONDITIONAL_FORM_ENABLED && (
        <Button
          onClick={(event) => {
            event.stopPropagation();
            setAnchorEl(event.currentTarget);
          }}
          style={{
            fontSize: "14px",
            textTransform: "none",
            color: "#478997",
          }}
        >
          {selectAllChecked
            ? "All Selected"
            : universalTagging?.length
              ? `${universalTagging?.length} Selected`
              : "Select Condition"}
        </Button>
      )}
      <Menu
        anchorEl={anchorEl}
        keepMounted
        open={Boolean(anchorEl)}
        onClose={(event, reason) => {
          if (reason === "backdropClick" || "escapeKeyDown") return;
          setAnchorEl(null);
        }}
        getContentAnchorEl={null}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
        PaperProps={{
          style: {
            minWidth: "400px",
            maxWidth: "600px",
            maxHeight: "500px",
          },
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <Box display="flex" alignItems="center" ml={2}>
          <Checkbox
            id="selectAllCheckbox"
            checked={selectAllChecked}
            onClick={(event) => event.stopPropagation()}
            onChange={(event) => {
              handleSelectAll(event, event.target.checked);
            }}
            size="small"
          />
          <Typography>All</Typography>
        </Box>
        <Box ml={4} mr={2} style={{ maxHeight: "360px", overflowY: "auto" }}>
          <TreeView
            className={classes.root}
            defaultCollapseIcon={<ExpandMoreIcon />}
            defaultExpandIcon={<ChevronRightIcon />}
            defaultExpanded={collectIds(
              conditionalFormQuestions?.options || [],
            )}
          >
            {conditionalFormQuestions?.options &&
              renderLevels(conditionalFormQuestions?.options)}
          </TreeView>
        </Box>
        <Box
          display="flex"
          justifyContent="flex-end"
          style={{
            padding: "12px 20px 8px",
            borderTop: "1px solid #e0e0e0",
            backgroundColor: "#fff",
          }}
        >
          <Button
            variant="contained"
            color="success"
            onClick={attachUniversalTagging}
          >
            Apply
          </Button>
        </Box>
      </Menu>
    </>
  );
};

export default ConditionalFormList;
